
import { useState, useEffect } from 'react';
// @ts-ignore
import { getNews, createNews, updateNews, deleteNews } from '../../../services/newsService';
// @ts-ignore
import uploadService from '../../../services/uploadService';
// @ts-ignore
import $ from 'jquery';
// @ts-ignore
import 'summernote/dist/summernote-lite.min.css';
// @ts-ignore
import 'summernote/dist/summernote-lite.min.js';

interface NewsItem {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  newCategoryId: string;
  author: string;
  readTime: string;
  image: string;
  tags: string[];
  status: string;
  publishDate: string;
  views: number;
  category: string;
}

const NewsManagement = () => {
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewingNews, setViewingNews] = useState<NewsItem | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
  const [newsList, setNewsList] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [imageError, setImageError] = useState<string>('');
  const itemsPerPage = 10;

  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    newCategoryId: '',
    author: '',
    readTime: '',
    image: '',
    tags: ['']
  });

  // Original helper functions and data

  // Lấy danh sách categories từ news data
  const getCategoriesFromNews = () => {
    const categoryMap = new Map();
    newsList.forEach(news => {
      if (news.newCategoryId && news.category) {
        categoryMap.set(news.newCategoryId, news.category);
      }
    });
    return Array.from(categoryMap.entries()).map(([id, name]) => ({ id, name }));
  };

  const categories = getCategoriesFromNews();

  // Load news data
  useEffect(() => {
    loadNews();
  }, []);

  // Initialize Summernote when modal opens
  useEffect(() => {
    if (isModalOpen) {
      // Initialize Summernote after a short delay to ensure DOM is ready
      const timer = setTimeout(() => {
        if ($('#summernote-editor').length && !$('#summernote-editor').hasClass('note-editable')) {
          $('#summernote-editor').summernote({
            height: 300,
            minHeight: 300,
            maxHeight: 500,
            focus: false,
            toolbar: [
              ['style', ['style']],
              ['font', ['bold', 'italic', 'underline', 'strikethrough', 'clear']],
              ['fontname', ['fontname']],
              ['fontsize', ['fontsize']],
              ['color', ['color']],
              ['para', ['ul', 'ol', 'paragraph']],
              ['table', ['table']],
              ['insert', ['link', 'picture', 'video']],
              ['view', ['fullscreen', 'codeview', 'help']]
            ],
            styleTags: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
            fontNames: ['Arial', 'Times New Roman', 'Georgia', 'Verdana', 'Courier New'],
            fontSizes: ['8', '9', '10', '11', '12', '14', '16', '18', '20', '24', '36', '48'],
            callbacks: {
              onInit: function() {
                // Summernote initialized
                
                // Set content immediately if in edit mode
                if (isEditMode && formData.content) {
                  setTimeout(() => {
                    // Use code method to set HTML content
                    $('#summernote-editor').summernote('code', formData.content);
                  }, 100);
                }
              },
              onChange: function(contents: string) {
                setFormData(prev => ({ ...prev, content: contents }));
              }
            }
          });
        }
      }, 100);

      return () => {
        clearTimeout(timer);
        if ($('#summernote-editor').length && $('#summernote-editor').hasClass('note-editable')) {
          $('#summernote-editor').summernote('destroy');
        }
      };
    }
  }, [isModalOpen, isEditMode]);

  // Set content immediately after Summernote is ready
  useEffect(() => {
    if (isModalOpen && isEditMode && formData.content) {
      const timer = setTimeout(() => {
        if ($('#summernote-editor').hasClass('note-editable')) {
          $('#summernote-editor').summernote('code', formData.content);
        } else {
          // If Summernote is not ready, try again
          const retryTimer = setTimeout(() => {
            if ($('#summernote-editor').hasClass('note-editable')) {
              $('#summernote-editor').summernote('code', formData.content);
            }
          }, 500);
          
          return () => clearTimeout(retryTimer);
        }
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [isModalOpen, isEditMode, formData.content]);

  // Force set content when formData.content changes
  useEffect(() => {
    if (isModalOpen && formData.content && $('#summernote-editor').hasClass('note-editable')) {
      $('#summernote-editor').summernote('code', formData.content);
    }
  }, [formData.content, isModalOpen]);

  const loadNews = async () => {
    try {
      setLoading(true);
      const response = await getNews();
      
      // Xử lý cấu trúc API response
      if (response.data && response.data.news) {
        // Transform data để match với interface
        const transformedNews = response.data.news.map((item: any) => ({
          id: item._id,
          title: item.title,
          excerpt: item.excerpt,
          content: item.content,
          newCategoryId: item.newCategoryId._id,
          author: item.author,
          readTime: item.readTime,
          image: item.image,
          tags: item.tags,
          status: 'published', // Default status vì API không trả về status
          publishDate: item.createdAt,
          views: 0, // Default views vì API không trả về views
          category: item.newCategoryId.name
        }));
        setNewsList(transformedNews);
      } else {
        setNewsList([]);
      }
    } catch (error) {
      console.error('Error loading news:', error);
    } finally {
      setLoading(false);
    }
  };

  // Validate image ratio
  const validateImageRatio = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const ratio = img.width / img.height;
          // Tỉ lệ từ 1.5:1 đến 2:1 (width/height từ 1.5 đến 2)
          const isValid = ratio >= 1.5 && ratio <= 2;
          resolve(isValid);
        };
        img.onerror = () => resolve(false);
        img.src = e.target?.result as string;
      };
      reader.onerror = () => resolve(false);
      reader.readAsDataURL(file);
    });
  };

  // Handle file selection
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate tỉ lệ ảnh trước
      const isValidRatio = await validateImageRatio(file);
      if (!isValidRatio) {
        setImageError('Ảnh phải có tỉ lệ từ 1.5:1 đến 2:1 (ví dụ: 1024x512px, 1500x1000px, 816x512px)');
        setSelectedFile(null);
        setPreviewImage('');
        // Reset file input
        e.target.value = '';
        return;
      }

      // Clear error nếu ảnh hợp lệ
      if (imageError) {
        setImageError('');
      }

      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle upload image
  const handleUploadImage = async (newsId: string) => {
    if (!selectedFile) return null;
    
    try {
      const response = await uploadService.uploadFile(selectedFile, newsId, 'News');
      return response.data?.url || response.url;
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  // Reset form data
  const resetFormData = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      newCategoryId: '',
      author: '',
      readTime: '',
      image: '',
      tags: ['']
    });
    setSelectedFile(null);
    setPreviewImage('');
    setImageError('');
  };

  // Open modal for add
  const handleAddNews = () => {
    setIsEditMode(false);
    resetFormData();
    setIsModalOpen(true);
  };

  // Open modal for edit
  const handleEditNews = (news: NewsItem) => {
    setIsEditMode(true);
    setSelectedNews(news);
    setFormData({
      title: news.title,
      excerpt: news.excerpt,
      content: news.content,
      newCategoryId: news.newCategoryId,
      author: news.author,
      readTime: news.readTime,
      image: news.image,
      tags: news.tags.length > 0 ? news.tags : ['']
    });
    setPreviewImage(news.image);
    setIsModalOpen(true);
  };

  // Handle view news details
  const handleViewNews = (news: NewsItem) => {
    setViewingNews(news);
    setIsViewModalOpen(true);
  };

  // Handle delete news
  const handleDeleteNews = async (id: string) => {
    if (!confirm('Bạn có chắc chắn muốn xóa bài viết này?')) return;
    
    try {
      setLoading(true);
      await deleteNews(id);
      await loadNews();
    } catch (error) {
      console.error('Error deleting news:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = () => {
    setFormData({ ...formData, tags: [...formData.tags, ''] });
  };

  const handleRemoveTag = (index: number) => {
    const newTags = formData.tags.filter((_, i) => i !== index);
    setFormData({ ...formData, tags: newTags });
  };

  const handleTagChange = (index: number, value: string) => {
    const newTags = [...formData.tags];
    newTags[index] = value;
    setFormData({ ...formData, tags: newTags });
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
    const filteredTags = formData.tags.filter(tag => tag.trim() !== '');
      
      let imageUrl = formData.image;

      if (isEditMode && selectedNews) {
        // Update existing news
        let updateData = {
      ...formData,
      tags: filteredTags
    };

        // Upload new image if selected
        if (selectedFile) {
          setUploading(true);
          imageUrl = await handleUploadImage(selectedNews.id);
          updateData.image = imageUrl;
        }

        await updateNews(selectedNews.id, updateData);
      } else {
        // Create new news - tạo news trước với ảnh mặc định
        const createData = {
          ...formData,
          tags: filteredTags,
          image: imageUrl || '' // Sử dụng ảnh hiện tại hoặc rỗng
        };

        const response = await createNews(createData);
        const newsId = response.data._id;
        
        // Upload image if selected
        if (selectedFile) {
          setUploading(true);
          imageUrl = await handleUploadImage(newsId);
          if (imageUrl) {
            await updateNews(newsId, { ...createData, image: imageUrl });
          }
        }
      }

      await loadNews();
      setIsModalOpen(false);
      resetFormData();
    } catch (error) {
      console.error('Error saving news:', error);
      alert('Có lỗi xảy ra khi lưu bài viết. Vui lòng thử lại.');
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  // Modified filtering & pagination logic
  const filteredNews = newsList.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.author.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || item.newCategoryId === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const totalPages = Math.ceil(filteredNews.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedNews = filteredNews.slice(startIndex, startIndex + itemsPerPage);

  // Placeholder for any additional existing code (none needed)

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Quản lý tin tức</h2>
          <p className="text-gray-600">Quản lý tất cả bài viết và tin tức</p>
        </div>
        <button
          onClick={handleAddNews}
          className="bg-[#2A4B7C] hover:bg-[#2A4B7C]/90 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Thêm bài viết
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Tìm kiếm bài viết..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="lg:w-48">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent pr-8"
            >
              <option value="all">Tất cả danh mục</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

        </div>
      </div>

      {/* News Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 w-1/3">Bài viết</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 w-20">Danh mục</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 w-32">Tác giả</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 w-28">Ngày tạo</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 w-24">Thao tác</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-gray-500">
                    Đang tải dữ liệu...
                  </td>
                </tr>
              ) : paginatedNews.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-gray-500">
                    Không có bài viết nào
                  </td>
                </tr>
              ) : (
                paginatedNews.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                    <td className="py-4 px-6 w-1/3 max-w-xs">
                    <div className="flex items-center gap-3">
                        {/* News image */}
                        <div className="w-12 h-12 bg-gray-200 rounded-lg overflow-hidden">
                          {item.image ? (
                            <img 
                              src={item.image} 
                              alt={item.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <i className="ri-image-line"></i>
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-medium text-gray-900 line-clamp-1 truncate">{item.title}</h3>
                          <p className="text-sm text-gray-600 line-clamp-2 truncate mt-1">{item.excerpt}</p>
                          <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-gray-500">{item.readTime}</span>
                            <div className="flex gap-1 flex-wrap">
                            {item.tags.slice(0, 2).map((tag, index) => (
                                <span key={index} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs whitespace-nowrap">
                                {tag}
                              </span>
                            ))}
                            {item.tags.length > 2 && (
                              <span className="text-xs text-gray-500">+{item.tags.length - 2}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                    <td className="py-4 px-6 w-20">
                    <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
                      {item.category}
                    </span>
                  </td>
                    <td className="py-4 px-6 w-32 text-gray-900">{item.author}</td>
                    <td className="py-4 px-6 w-28 text-gray-600">
                      {new Date(item.publishDate).toLocaleDateString('vi-VN', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                      })}
                  </td>
                    <td className="py-4 px-6 w-24">
                    <div className="flex items-center gap-2">
                      <button
                          onClick={() => handleEditNews(item)}
                        className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                        <button 
                          onClick={() => handleViewNews(item)}
                          className="w-8 h-8 flex items-center justify-center text-green-600 hover:bg-green-50 rounded-lg transition-colors cursor-pointer"
                        >
                        <i className="ri-eye-line"></i>
                      </button>
                      <button
                          onClick={() => handleDeleteNews(item.id)}
                        className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Hiển thị {startIndex + 1} - {Math.min(startIndex + itemsPerPage, filteredNews.length)} của {filteredNews.length} bài viết
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Trước
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 border rounded-lg text-sm ${
                      currentPage === page
                        ? 'bg-[#2A4B7C] text-white border-[#2A4B7C]'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Sau
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* News Modal (Add/Edit) */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">
                  {isEditMode ? 'Chỉnh sửa bài viết' : 'Viết bài mới'}
                </h3>
                <button 
                  onClick={() => {
                    setIsModalOpen(false);
                    resetFormData();
                  }}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tiêu đề bài viết *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                    placeholder="Nhập tiêu đề bài viết"
                  />
                </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Danh mục *
                    </label>
                    <select 
                      required
                      value={formData.newCategoryId}
                      onChange={(e) => setFormData({ ...formData, newCategoryId: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent pr-8"
                    >
                      <option value="">Chọn danh mục</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tác giả *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.author}
                      onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                      placeholder="Nhập tên tác giả"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Thời gian đọc *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.readTime}
                      onChange={(e) => setFormData({ ...formData, readTime: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                      placeholder="VD: 5 phút đọc"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mô tả ngắn *
                  </label>
                  <textarea
                    rows={3}
                    required
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent resize-none"
                    placeholder="Mô tả ngắn gọn về nội dung bài viết..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags
                  </label>
                  <div className="space-y-2">
                    {formData.tags.map((tag, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          value={tag}
                          onChange={(e) => handleTagChange(index, e.target.value)}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                          placeholder="Nhập tag"
                        />
                        {formData.tags.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(index)}
                            className="w-10 h-10 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="text-[#2A4B7C] hover:text-[#2A4B7C]/80 font-medium flex items-center gap-2 cursor-pointer"
                    >
                      <i className="ri-add-line"></i>
                      Thêm tag
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ảnh đại diện
                  </label>
                  
                  {/* File upload */}
                  <div className="mb-4">
                  <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent ${
                      imageError ? 'border-red-500' : 'border-gray-300'
                    }`}
                    />
                    {selectedFile && !imageError && (
                      <p className="text-sm text-green-600 mt-2">
                        <i className="ri-check-line mr-1"></i>
                        Đã chọn: {selectedFile.name}
                      </p>
                    )}
                    {imageError && (
                      <p className="text-sm text-red-600 mt-2">
                        {imageError}
                      </p>
                    )}
                    {!imageError && !selectedFile && (
                      <p className="text-sm text-gray-500 mt-2">
                        Tỉ lệ ảnh yêu cầu: từ 1.5:1 đến 2:1 (ví dụ: 1024x512px, 1500x1000px, 816x512px)
                      </p>
                    )}
                  </div>

                  {/* Image preview */}
                  {(previewImage || formData.image) && (
                    <div className="mb-4">
                      <img
                        src={previewImage || formData.image}
                        alt="Preview"
                        className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                      />
                    </div>
                  )}

                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nội dung bài viết *
                  </label>
                  
                  {/* Editor Guide */}
                  <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <i className="ri-information-line text-blue-600 mt-0.5"></i>
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">Hướng dẫn sử dụng editor:</p>
                        <ul className="space-y-1 text-xs">
                          <li>• <strong>Style:</strong> Chọn tiêu đề H1, H2, H3, H4, H5, H6</li>
                          <li>• <strong>Font:</strong> Bold, Italic, Underline, Strikethrough</li>
                          <li>• <strong>Font Name:</strong> Arial, Times New Roman, Georgia, Verdana, Courier New</li>
                          <li>• <strong>Font Size:</strong> 8px đến 48px</li>
                          <li>• <strong>Color:</strong> Chọn màu chữ và màu nền</li>
                          <li>• <strong>Paragraph:</strong> Danh sách, căn lề</li>
                          <li>• <strong>Table:</strong> Chèn bảng</li>
                          <li>• <strong>Insert:</strong> Link, hình ảnh, video</li>
                          <li>• <strong>View:</strong> Fullscreen, code view, help</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border border-gray-300 rounded-lg">
                    <div 
                      id="summernote-editor"
                      className="summernote-editor"
                      style={{ minHeight: '300px' }}
                    />
                    <style dangerouslySetInnerHTML={{
                      __html: `
                        .summernote-editor .note-editable {
                          font-family: Arial, sans-serif !important;
                          line-height: 1.6 !important;
                        }
                        .summernote-editor .note-editable h1 {
                          font-size: 2.5em !important;
                          font-weight: bold !important;
                          margin: 0.67em 0 !important;
                          color: #333 !important;
                        }
                        .summernote-editor .note-editable h2 {
                          font-size: 2em !important;
                          font-weight: bold !important;
                          margin: 0.75em 0 !important;
                          color: #333 !important;
                        }
                        .summernote-editor .note-editable h3 {
                          font-size: 1.5em !important;
                          font-weight: bold !important;
                          margin: 0.83em 0 !important;
                          color: #333 !important;
                        }
                        .summernote-editor .note-editable h4 {
                          font-size: 1.25em !important;
                          font-weight: bold !important;
                          margin: 1.12em 0 !important;
                          color: #333 !important;
                        }
                        .summernote-editor .note-editable h5 {
                          font-size: 1.1em !important;
                          font-weight: bold !important;
                          margin: 1.5em 0 !important;
                          color: #333 !important;
                        }
                        .summernote-editor .note-editable h6 {
                          font-size: 1em !important;
                          font-weight: bold !important;
                          margin: 1.67em 0 !important;
                          color: #333 !important;
                        }
                        .summernote-editor .note-editable p {
                          margin: 1em 0 !important;
                          font-size: 16px !important;
                          line-height: 1.6 !important;
                        }
                        .summernote-editor .note-editable strong,
                        .summernote-editor .note-editable b {
                          font-weight: bold !important;
                        }
                        .summernote-editor .note-editable em,
                        .summernote-editor .note-editable i {
                          font-style: italic !important;
                        }
                        .summernote-editor .note-editable u {
                          text-decoration: underline !important;
                        }
                        .summernote-editor .note-editable s {
                          text-decoration: line-through !important;
                        }
                        .summernote-editor .note-editable ul {
                          list-style-type: disc !important;
                          margin: 1em 0 !important;
                          padding-left: 2em !important;
                        }
                        .summernote-editor .note-editable ol {
                          list-style-type: decimal !important;
                          margin: 1em 0 !important;
                          padding-left: 2em !important;
                        }
                        .summernote-editor .note-editable li {
                          margin: 0.5em 0 !important;
                        }
                        .summernote-editor .note-editable table {
                          border-collapse: collapse !important;
                          width: 100% !important;
                          margin: 1em 0 !important;
                        }
                        .summernote-editor .note-editable table td,
                        .summernote-editor .note-editable table th {
                          border: 1px solid #ddd !important;
                          padding: 8px !important;
                        }
                        .summernote-editor .note-editable table th {
                          background-color: #f2f2f2 !important;
                          font-weight: bold !important;
                        }
                      `
                    }} />
                  </div>
                </div>
                
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading || uploading}
                    className="flex-1 bg-[#2A4B7C] hover:bg-[#2A4B7C]/90 text-white py-3 rounded-lg font-semibold transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading || uploading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        {uploading ? 'Đang upload ảnh...' : 'Đang xử lý...'}
                      </>
                    ) : (
                      isEditMode ? 'Cập nhật bài viết' : 'Xuất bản bài viết'
                    )}
                  </button>
                  <button
                    type="button"
                    disabled={loading || uploading}
                    className="px-6 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Lưu nháp
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsModalOpen(false);
                      resetFormData();
                    }}
                    disabled={loading || uploading}
                    className="px-6 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Hủy
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View News Modal */}
      {isViewModalOpen && viewingNews && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Chi tiết bài viết</h3>
                <button 
                  onClick={() => setIsViewModalOpen(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              
              <div className="space-y-6">
                {/* News Image */}
                {viewingNews.image && (
                  <div className="w-full h-64 bg-gray-200 rounded-lg overflow-hidden">
                    <img 
                      src={viewingNews.image} 
                      alt={viewingNews.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* News Content */}
                <div className="space-y-4">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">{viewingNews.title}</h1>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                      <span>Tác giả: <strong>{viewingNews.author}</strong></span>
                      <span>•</span>
                      <span>{viewingNews.readTime}</span>
                      <span>•</span>
                      <span>{new Date(viewingNews.publishDate).toLocaleDateString('vi-VN')}</span>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Mô tả ngắn</h3>
                    <p className="text-gray-700 leading-relaxed">{viewingNews.excerpt}</p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {viewingNews.tags.map((tag, index) => (
                        <span key={index} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Nội dung</h3>
                    <div 
                      className="prose max-w-none text-gray-700 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: viewingNews.content }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsManagement;
